<template>
  <div id="app-wrapper">
    <canvas ref="starrySkyCanvas" class="starry-sky-canvas"></canvas>
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import EmptyLayout from '@/layouts/EmptyLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue'; // Uvoz novega layouta
import axios from 'axios';

export default {
  components: {
    DefaultLayout,
    EmptyLayout,
    AuthLayout
  },
  data() {
    return {
      animationFrameId: null,
      resizeHandler: null,
    };
  },
  computed: {
    layout() {
      // Vrne ime komponente layouta iz meta podatkov poti ali privzeto
      return this.$route.meta.layout || 'DefaultLayout';
    }
  },
  created() {
    axios.interceptors.response.use(
        response => response,
        error => {
          if (error.response && error.response.status === 401) {
            console.log('Token expired, logging out user');
            this.$store.dispatch('auth/handleExpiredToken');
            this.$router.push('/login');
            window.location.reload();
          }
          return Promise.reject(error);
        }
    );
  },
  mounted() {
    this.initStarrySky();
  },
  beforeUnmount() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  },
  methods: {
    initStarrySky() {
      const canvas = this.$refs.starrySkyCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      let width, height;
      let stars = [];
      let shootingStars = [];
      const numStars = 400;

      const random = (min, max) => Math.random() * (max - min) + min;

      const createStar = () => ({
        x: random(0, width),
        y: random(0, height),
        radius: random(0.5, 1.5),
        alpha: random(0.3, 1),
        twinkleFactor: random(0.01, 0.03),
        twinkles: Math.random() > 0.3, // ~70% of stars will twinkle
      });

      const createShootingStar = () => {
        const isHorizontal = Math.random() > 0.5;
        const startX = isHorizontal ? (Math.random() > 0.5 ? 0 : width) : random(0, width);
        const startY = isHorizontal ? random(0, height) : (Math.random() > 0.5 ? 0 : height);
        const endX = random(width * 0.25, width * 0.75);
        const endY = random(height * 0.25, height * 0.75);
        const angle = Math.atan2(endY - startY, endX - startX);
        return {
          x: startX,
          y: startY,
          len: random(100, 200),
          speed: random(1, 3), // Slower speed
          angle: angle,
          opacity: 1,
          life: 0,
          maxLife: random(200, 400), // Longer life
        };
      };

      const init = () => {
        width = window.innerWidth;
        height = window.innerHeight;
        canvas.width = width;
        canvas.height = height;
        stars = Array.from({ length: numStars }, createStar);
        shootingStars = [];
      };

      const update = () => {
        stars.forEach(star => {
          if (star.twinkles) {
            star.alpha += star.twinkleFactor;
            if (star.alpha > 1 || star.alpha < 0.3) {
              star.twinkleFactor = -star.twinkleFactor;
            }
          }
        });

        shootingStars.forEach((ss, index) => {
          ss.x += Math.cos(ss.angle) * ss.speed;
          ss.y += Math.sin(ss.angle) * ss.speed;
          ss.life++;
          ss.opacity = 1 - (ss.life / ss.maxLife);

          if (ss.opacity <= 0) {
            shootingStars.splice(index, 1);
          }
        });

        if (Math.random() > 0.995) {
          shootingStars.push(createShootingStar());
        }
      };

      const draw = () => {
        ctx.clearRect(0, 0, width, height);

        const gradient = ctx.createRadialGradient(width / 2, height, 0, width / 2, height, height);
        gradient.addColorStop(0, '#1B2735');
        gradient.addColorStop(1, '#090A0F');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        stars.forEach(star => {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.radius, 0, 2 * Math.PI);
          ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha})`;
          ctx.fill();
        });

        shootingStars.forEach(ss => {
          const grad = ctx.createLinearGradient(ss.x, ss.y, ss.x - ss.len * Math.cos(ss.angle), ss.y - ss.len * Math.sin(ss.angle));
          grad.addColorStop(0, `rgba(255, 255, 255, ${ss.opacity})`);
          grad.addColorStop(1, 'rgba(255, 255, 255, 0)');

          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(ss.x, ss.y);
          ctx.lineTo(ss.x - ss.len * Math.cos(ss.angle), ss.y - ss.len * Math.sin(ss.angle));
          ctx.stroke();
        });
      };

      const animate = () => {
        update();
        draw();
        this.animationFrameId = requestAnimationFrame(animate);
      };

      this.resizeHandler = () => init();

      init();
      animate();
      window.addEventListener('resize', this.resizeHandler);
    }
  }
}
</script>

<style>
#app-wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
}

.starry-sky-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  display: block;
}
</style>